/* eslint-disable max-lines */
import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslate } from '@im/react-localization';
import Button from '@mui/material/Button';
import { useProgramListQuery } from 'src/hooks/program-list';
import { useSearchParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import { useVendorSearchQuery } from 'src/views/vendorprogram/hooks/apiHook';
import ClearIcon from '@mui/icons-material/Clear';
import LabeledField from 'src/components/Form/LabeledField';
import PresetDateRangePicker from 'src/components/DateRangePicker/PresetDateRangePicker';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import Checkbox from '@mui/material/Checkbox';

interface Props {
  searchQuery: {
    AcopStatus?: string;
    AcopTracking?: string;
    BCN?: string;
    BidNumber?: string;
    BundleId?: string;
    IsOpenOrdersOnly?: boolean;
    OrderEndDate?: string;
    OrderNumber?: string;
    OrderStartDate?: string;
    Program?: number;
    Vendor?: number;
  };
  setPageSetUp: React.Dispatch<
    React.SetStateAction<{
      page: number;
      pageSize: number;
    }>
  >;
  setSearchQuery: React.Dispatch<
    React.SetStateAction<Partial<Props['searchQuery']>>
  >;
}

interface Program {
  id: number;
  name: string;
}

interface StatusOption {
  label: string;
  value: string;
}

const SearchAcopsFields: React.FC<Props> = ({
  searchQuery,
  setPageSetUp,
  setSearchQuery,
}) => {
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode') ?? '';

  const [vendorInput, setVendorInput] = useState<string>('');
  const [fieldValues, setFieldValues] = useState(searchQuery);
  const [vendorId, setVendorId] = useState<number>(0);
  const [isOpenOrdersOnlyChecked, setIsOpenOrdersOnlyChecked] = useState(false);

  const { data: vendorList = [], isLoading: isVendorLoading } =
    useVendorSearchQuery({
      countryCode,
      vendorNameToSearch: vendorInput,
    });

  const { data: programList, isLoading: isProgramLoading } =
    useProgramListQuery({
      countryCode,
      vendorIdToSearch: vendorId,
    });

  const acopStatusList: StatusOption[] = ['Active', 'Pending', 'Expired'].map(
    (value) => ({
      label: translate(`AcopMaintenance.${value}DdlStatusesName`),
      value,
    })
  );

  const programs: Program[] =
    programList?.vendorProgramlist?.map((p) => ({
      id: p.vendorProgramId,
      name: p.vendorProgramName,
    })) ?? [];

  const handleFieldChange = (key: string, value: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'Vendor' && { Program: undefined }),
    }));
  };

  const handleSearch = () => {
    setPageSetUp({ page: 1, pageSize: 10 });
    setSearchQuery(fieldValues);
    searchParams.delete('dmrId');
    setSearchParams(searchParams, { replace: true });
  };

  const textFields: {
    key: keyof typeof searchQuery;
    label: string;
    type?: string;
  }[] = [
    { key: 'BidNumber', label: 'BidNumber' },
    { key: 'BCN', label: 'BCN' },
    { key: 'OrderNumber', label: 'OrderNumber' },
    { key: 'AcopTracking', label: 'AcopTracking', type: 'number' },
    { key: 'BundleId', label: 'BundleId' },
  ];

  const handleOpenOrderChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsOpenOrdersOnlyChecked(event.target.checked);
    setFieldValues((prev) => ({
      ...prev,
      IsOpenOrdersOnly: event.target.checked,
    }));
  };

  return (
    <Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        mb={2}
        sx={{
          '.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
            { padding: '6px 8px' },
          '.MuiCheckbox-root': {
            width: 'fit-content',
          },
          '.MuiFormControl-root': { width: '280px' },
        }}
      >
        <FormControl>
          <LabeledField
            isRequired
            label={translate('AcopMaintenance.ColumnVendor')}
          >
            <Autocomplete
              data-testid={'Autocomplete-Vendor'}
              freeSolo
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.vendorName
              }
              id="Vendor"
              inputValue={vendorInput}
              isOptionEqualToValue={(o, v) => o.vendorID === v.vendorID}
              loading={isVendorLoading}
              onChange={(_, value, reason) => {
                if (
                  value &&
                  typeof value !== 'string' &&
                  reason === 'selectOption'
                ) {
                  const id = value.vendorID;
                  setVendorId(id);
                  handleFieldChange('Vendor', id);
                } else if (reason === 'clear') {
                  setVendorId(0);
                  handleFieldChange('Vendor', undefined);
                }
              }}
              onInputChange={(_, value) => setVendorInput(value)}
              options={vendorList}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate('Common.SearchValue', {
                    value: translate('AcopMaintenance.ColumnVendor'),
                  })}
                />
              )}
              value={
                vendorList.find((v) => v.vendorID === fieldValues.Vendor) ??
                null
              }
            />
          </LabeledField>
        </FormControl>
        <FormControl>
          <LabeledField label={translate('AcopMaintenance.ColumnProgram')}>
            <Autocomplete
              data-testid="Autocomplete-Program"
              disabled={!fieldValues.Vendor}
              getOptionLabel={(option) => option.name}
              id="Program"
              loading={isProgramLoading}
              onChange={(_, value, reason) =>
                handleFieldChange(
                  'Program',
                  reason === 'clear' ? undefined : value?.id
                )
              }
              options={programs}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate('Common.SelectValue', {
                    value: translate('AcopMaintenance.ColumnProgram'),
                  })}
                />
              )}
              value={programs.find((p) => p.id === fieldValues.Program) ?? null}
            />
          </LabeledField>
        </FormControl>
        <FormControl>
          <LabeledField label={translate('AcopMaintenance.ColumnAcopStatus')}>
            <Autocomplete
              data-testid="Autocomplete-AcopStatus"
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              onChange={(_, value, reason) =>
                handleFieldChange(
                  'AcopStatus',
                  reason === 'clear' ? undefined : value?.value
                )
              }
              options={acopStatusList}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate('Common.SelectValue', {
                    value: translate('AcopMaintenance.ColumnAcopStatus'),
                  })}
                />
              )}
              value={
                acopStatusList.find(
                  (s) => s.value === fieldValues.AcopStatus
                ) ?? null
              }
            />
          </LabeledField>
        </FormControl>
        {textFields.map(({ key, label, type }) => (
          <FormControl key={key}>
            <LabeledField label={translate(`AcopMaintenance.Column${label}`)}>
              <Autocomplete
                data-testid={`Autocomplete-${key}`}
                freeSolo
                inputValue={fieldValues[key]?.toString() ?? ''}
                onInputChange={(_, value, reason) =>
                  handleFieldChange(key, reason === 'clear' ? undefined : value)
                }
                options={[]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={translate('Common.EnterValue', {
                      value: translate(`AcopMaintenance.Column${label}`),
                    })}
                    type={type}
                  />
                )}
              />
            </LabeledField>
          </FormControl>
        ))}
        <FormControl>
          <LabeledField label="Order date range">
            <PresetDateRangePicker
              dateValues={{
                FromDate: fieldValues.OrderStartDate,
                ToDate: fieldValues.OrderEndDate,
              }}
              setDateValues={(values) => {
                setFieldValues((prev) => ({
                  ...prev,
                  OrderEndDate: dayjs(values.ToDate).format('YYYY-MM-DD'),
                  OrderStartDate: dayjs(values.FromDate).format('YYYY-MM-DD'),
                }));
              }}
            />
          </LabeledField>
        </FormControl>
        <FormControl>
          <LabeledField label={translate('AcopMaintenance.OpenOrder')}>
            <Checkbox
              checked={isOpenOrdersOnlyChecked}
              onChange={handleOpenOrderChange}
            />
          </LabeledField>
        </FormControl>
      </Stack>
      <Stack direction="row" justifyContent="space-between" mt={4}>
        <Typography fontStyle={'italic'} variant="body2">
          {translate('AcopMaintenance.SearchFieldshintText')}
        </Typography>
        <Stack direction={'row'} gap={2}>
          <Button
            disabled={!fieldValues.Vendor}
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            variant="newContained"
          >
            {translate('Common.Search')}
          </Button>
          <Button
            onClick={() => {
              setFieldValues({});
              setSearchQuery({});
              setVendorInput('');
              setVendorId(0);
              setIsOpenOrdersOnlyChecked(false);
            }}
            startIcon={<ClearIcon color="error" />}
            variant="newOutlined"
          >
            {translate('Common.ClearAll')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SearchAcopsFields;
